import React, { useCallback, PropsWithChildren } from 'react';
import { Button, ButtonProps, WithSystemProps } from '@landr/maestro';
import { useModalContext } from 'contexts/ModalContext/ModalContext';
import { AuthenticatedActions } from 'types';
import { usePrivateAction } from 'hooks/usePrivateAction';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { useConvertExperiment } from 'utils/convert/hooks/useConvertExperiment';
import { CONFIG } from 'utils/config';
import { useUnlockPlanRedirect } from 'components/Navigations/MainNavigation/hooks/useUnlockPlanRedirect';
import { useAppSettings } from 'contexts/AppSettingsContext/AppSettingsContext';

type AnalyticVariant =
    | 'UpsellBanner'
    | 'FreemiumGatingBanner'
    | 'PaginationGated'
    | 'RemainingCredits'
    | 'PartnerPaginationGated'
    | 'PartnerUpsell'
    | 'PartnerFreemiumGatingBanner';

const getAnalytics = (analyticVariant: AnalyticVariant) => {
    switch (analyticVariant) {
        case 'PaginationGated':
            return {
                ctaType: CtaType.Button,
                interactionSource: InteractionSource.SampleTable,
                ctaText: 'Start free trial to unlock',
            };
        case 'PartnerPaginationGated':
            return {
                ctaType: CtaType.Button,
                interactionSource: InteractionSource.SampleTable,
                ctaText: 'Unlock with a plan',
            };
        case 'FreemiumGatingBanner':
            return {
                ctaType: CtaType.Card,
                interactionSource: InteractionSource.CardAction,
                ctaText: 'Start free 3-day trial',
            };
        case 'PartnerFreemiumGatingBanner':
            return {
                ctaType: CtaType.Card,
                interactionSource: InteractionSource.CardAction,
                ctaText: 'Unlock with a plan',
            };
        case 'UpsellBanner':
            return {
                ctaType: CtaType.Button,
                interactionSource: InteractionSource.UpsellBanner,
                ctaText: 'Start free trial',
            };
        case 'PartnerUpsell':
            return {
                ctaType: CtaType.Button,
                interactionSource: InteractionSource.UpsellBanner,
                ctaText: 'Unlock with a plan',
            };
        case 'RemainingCredits':
            return {
                ctaType: CtaType.Button,
                interactionSource: InteractionSource.Navbar,
                ctaText: 'Credits balance',
            };
    }
};

export const useFreemiumGatingCta = ({
    analyticVariant,
}: {
    analyticVariant: AnalyticVariant;
}) => {
    const { openModal } = useModalContext();
    const redirectToPricing = useUnlockPlanRedirect();

    const {
        VITE_FREE_TRIAL_MODAL_EXPERIMENT_ID: experimentId,
        VITE_FREE_TRIAL_MODAL_VARIANT_ID: variationId,
        VITE_FREE_TRIAL_MODAL_ORIGINAL_ID: originalId,
    } = CONFIG;

    const { isVariantId } = useConvertExperiment({
        experimentId,
        variationId,
    });

    const { showFeaturedContent } = useAppSettings();

    const doPrivateAction = usePrivateAction();

    const handleOnClick = useCallback(() => {
        const { ctaType, interactionSource, ctaText } =
            getAnalytics(analyticVariant);

        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.StartTrial,
            ctaType,
            interactionSource,
            ctaText,
            experimentVariant: isVariantId
                ? `${experimentId}.${variationId}`
                : `${experimentId}.${originalId}`,
        });

        const callback = showFeaturedContent
            ? () => openModal.trialUpsellModal()
            : () => redirectToPricing();
        if (isVariantId) {
            callback();
        } else {
            doPrivateAction({
                handleAuthenticated: () => callback(),
                actionDescription: {
                    type: AuthenticatedActions.openTrialUpsellModal,
                    args: [],
                },
            });
        }
    }, [
        analyticVariant,
        isVariantId,
        experimentId,
        variationId,
        originalId,
        openModal,
        doPrivateAction,
    ]);

    return { handleOnClick };
};

interface FreemiumGatingCtaProps extends ButtonProps {
    analyticVariant: AnalyticVariant;
    isEligibleForTrial: boolean | undefined;
}

const FreemiumGatingCta: React.FC<
    PropsWithChildren<FreemiumGatingCtaProps & WithSystemProps<ButtonProps>>
> = ({ children, analyticVariant, isEligibleForTrial, ...rest }) => {
    const unlockWithPlanRedirect = useUnlockPlanRedirect();
    const { handleOnClick } = useFreemiumGatingCta({
        analyticVariant,
    });

    return (
        <Button
            onClick={
                isEligibleForTrial ? handleOnClick : unlockWithPlanRedirect
            }
            {...rest}
        >
            {children}
        </Button>
    );
};

export default FreemiumGatingCta;
